import { utils } from "ethers";

// Builder Codes admin helpers (V1 + V2 self-service oversight). The backend contract lives in
// exchange-backend apps/admin/builders.go + services/system/builder.go.

export type BuilderStatus = "ACTIVE" | "SUSPENDED" | "DISABLED";

export const BUILDER_STATUSES: BuilderStatus[] = [
  "ACTIVE",
  "SUSPENDED",
  "DISABLED",
];

// A new builder may only be created ACTIVE or SUSPENDED.
export const BUILDER_CREATION_STATUSES: BuilderStatus[] = [
  "ACTIVE",
  "SUSPENDED",
];

// Protocol ceiling for max_fee_rate_perps (1% = 100 bps), in micro-units.
const FEE_RATE_SCALE = 1_000_000;
const MAX_FEE_RATE_MICROS = 10_000;

// Mirrors services/system BuilderFeeRatePattern: canonical plain decimal,
// no leading zeros, at most 6 fractional digits.
const FEE_RATE_PATTERN = /^(0|[1-9][0-9]*)(\.[0-9]{1,6})?$/;

export const BUILDER_MAX_NAME_LENGTH = 64;

// Self-service min_create_balance: human-readable USDC, 6 dp, <= 1,000,000
// (services/system ParseBuilderMinCreateBalance).
const MAX_MIN_CREATE_BALANCE_MICROS = 1_000_000 * FEE_RATE_SCALE;

// Options builder fees (services/system builder config): max_fee_rate_options
// is a fraction of index notional (<= 0.01); option_premium_cap is a
// fraction of premium (<= 0.25).
const MAX_OPTION_PREMIUM_CAP_MICROS = 250_000;

// Worked example shown in the options confirmations.
export const OPTION_FEE_EXAMPLE = {
  indexPrice: 2700,
  contracts: 1,
  premium: 5,
};

/**
 * Parses a canonical fee-rate decimal string into integer micro-units
 * (1e-6), avoiding floating point. Returns undefined if the string is not
 * in the canonical form accepted by the backend.
 */
export const parseFeeRateMicros = (rate: string): number | undefined => {
  if (!FEE_RATE_PATTERN.test(rate)) {
    return undefined;
  }
  const [whole, fraction = ""] = rate.split(".");
  const micros =
    Number(whole) * FEE_RATE_SCALE + Number(fraction.padEnd(6, "0"));
  if (!Number.isSafeInteger(micros)) {
    return undefined;
  }
  return micros;
};

/**
 * Validates a max_fee_rate_perps input. Returns an error message, or
 * undefined if valid (0 <= x <= 0.01, canonical decimal, <= 6 dp).
 */
export const validateFeeRate = (rate: string): string | undefined => {
  if (rate === "") {
    return "Fee rate is required";
  }
  if (!FEE_RATE_PATTERN.test(rate)) {
    return "Must be a plain decimal with at most 6 decimal places (e.g. 0.001)";
  }
  const micros = parseFeeRateMicros(rate);
  if (micros === undefined || micros > MAX_FEE_RATE_MICROS) {
    return "Must be between 0 and 0.01 (100 bps)";
  }
  return undefined;
};

// Formats integer micro-units divided by `divisor` as an exact decimal string.
const formatMicros = (micros: number, divisor: number): string => {
  const whole = Math.floor(micros / divisor);
  const remainder = micros % divisor;
  if (remainder === 0) {
    return String(whole);
  }
  const digits = String(divisor).length - 1;
  const fraction = String(remainder).padStart(digits, "0").replace(/0+$/, "");
  return `${whole}.${fraction}`;
};

/** "0.001" -> "10" (bps). Returns undefined for invalid input. */
export const feeRateToBps = (rate: string): string | undefined => {
  const micros = parseFeeRateMicros(rate);
  return micros === undefined ? undefined : formatMicros(micros, 100);
};

/** "0.001" -> "0.1" (percent). Returns undefined for invalid input. */
export const feeRateToPercent = (rate: string): string | undefined => {
  const micros = parseFeeRateMicros(rate);
  return micros === undefined ? undefined : formatMicros(micros, 10_000);
};

/** "0.001" -> "0.1% (10 bps)". Falls back to the raw value if unparseable. */
export const formatFeeRate = (rate: string): string => {
  const percent = feeRateToPercent(rate);
  const bps = feeRateToBps(rate);
  if (percent === undefined || bps === undefined) {
    return rate;
  }
  return `${percent}% (${bps} bps)`;
};

/**
 * Validates that the fee account is an exact EIP-55 checksum address. Does
 * NOT silently fix the casing: a lowercase/mis-cased address is an error
 * that tells the admin what the checksum form is.
 */
export const validateChecksumAddress = (address: string): string | undefined => {
  if (address === "") {
    return "Fee account is required";
  }
  let checksummed: string;
  try {
    checksummed = utils.getAddress(address);
  } catch {
    return "Not a valid address (bad format or checksum)";
  }
  if (checksummed !== address) {
    return `Address must be EIP-55 checksummed: ${checksummed}`;
  }
  return undefined;
};

export const validateBuilderName = (name: string): string | undefined => {
  if (name === "") {
    return "Name is required";
  }
  if (name.trim() !== name) {
    return "Name must not have leading or trailing whitespace";
  }
  if (name.length > BUILDER_MAX_NAME_LENGTH) {
    return `Name must be at most ${BUILDER_MAX_NAME_LENGTH} characters`;
  }
  return undefined;
};

/**
 * Validates a self-service min_create_balance input (USDC): canonical
 * decimal, at most 6 dp, 0 <= x <= 1,000,000.
 */
export const validateMinCreateBalance = (balance: string): string | undefined => {
  if (balance === "") {
    return "Minimum balance is required";
  }
  if (!FEE_RATE_PATTERN.test(balance)) {
    return "Must be a plain decimal with at most 6 decimal places (e.g. 100)";
  }
  const micros = parseFeeRateMicros(balance);
  if (micros === undefined || micros > MAX_MIN_CREATE_BALANCE_MICROS) {
    return "Must be between 0 and 1,000,000 USDC";
  }
  return undefined;
};

/**
 * Validates an option_premium_cap input: canonical decimal, at most 6 dp,
 * 0 <= x <= 0.25 (25% of premium).
 */
export const validateOptionPremiumCap = (cap: string): string | undefined => {
  if (cap === "") {
    return "Premium cap is required";
  }
  if (!FEE_RATE_PATTERN.test(cap)) {
    return "Must be a plain decimal with at most 6 decimal places (e.g. 0.05)";
  }
  const micros = parseFeeRateMicros(cap);
  if (micros === undefined || micros > MAX_OPTION_PREMIUM_CAP_MICROS) {
    return "Must be between 0 and 0.25 (25% of premium)";
  }
  return undefined;
};

/**
 * Upper bound of the builder fee on the OPTION_FEE_EXAMPLE fill, in USD:
 * min(rate x index x contracts, premiumCap x premium x contracts) — the same
 * formula as Aevo's own option fee. A side that is unset/invalid is skipped;
 * returns undefined if neither side is usable. Exact (integer micro-units).
 */
export const optionBuilderFeeExample = (
  rate: string | null | undefined,
  premiumCap: string | null | undefined
): string | undefined => {
  const { indexPrice, contracts, premium } = OPTION_FEE_EXAMPLE;
  const bounds: number[] = [];
  const rateMicros = rate ? parseFeeRateMicros(rate) : undefined;
  if (rateMicros !== undefined) {
    bounds.push(rateMicros * indexPrice * contracts);
  }
  const capMicros = premiumCap ? parseFeeRateMicros(premiumCap) : undefined;
  if (capMicros !== undefined) {
    bounds.push(capMicros * premium * contracts);
  }
  if (!bounds.length) {
    return undefined;
  }
  return formatMicros(Math.min(...bounds), FEE_RATE_SCALE);
};

/** "0.05" -> "5% of premium". Falls back to the raw value if unparseable. */
export const formatPremiumCap = (cap: string): string => {
  const percent = feeRateToPercent(cap);
  return percent === undefined ? cap : `${percent}% of premium`;
};

/** Statuses an existing builder may move to. */
export const allowedStatusTransitions = (
  current: BuilderStatus
): BuilderStatus[] => ({
  ACTIVE: BUILDER_STATUSES,
  SUSPENDED: BUILDER_STATUSES,
  DISABLED: BUILDER_STATUSES,
}[current]);

const BUILDER_ERROR_MESSAGES: Record<string, string> = {
  BUILDER_INVALID_ID: "Invalid builder id",
  BUILDER_INVALID_NAME:
    "Invalid name (required, no leading/trailing spaces, max 64 characters)",
  BUILDER_INVALID_FEE_ACCOUNT:
    "Invalid fee account (must be an EIP-55 checksum address of an existing Aevo account)",
  BUILDER_FEE_ACCOUNT_IN_USE:
    "Fee account is already used by another builder",
  BUILDER_INVALID_STATUS:
    "Invalid status (new builders must be ACTIVE or SUSPENDED)",
  BUILDER_INVALID_FEE_RATE:
    "Invalid fee rate (0 to 0.01, at most 6 decimal places)",
  BUILDER_NOT_FOUND: "Builder not found",
  INVALID_AMOUNT:
    "Invalid amount (0 to 1,000,000, at most 6 decimal places)",
  INVALID_PERIOD: "Invalid stats period",
};

/** Maps a server error code to a readable message (keeps the code visible). */
export const builderErrorMessage = (code?: string): string => {
  if (!code) {
    return "Unknown error";
  }
  const message = BUILDER_ERROR_MESSAGES[code];
  return message ? `${message} [${code}]` : code;
};

// ---------------------------------------------------------------------------
// Partner handoff links (see the exchange-backend builder docs, "Partner
// Handoff"). Query params match the exchange frontend BuilderApprovePage.
// ---------------------------------------------------------------------------

/**
 * Validates a partner approval max_fee_rate: canonical decimal, <= 6 dp,
 * 0 <= rate <= protocol cap.
 */
export const validatePartnerFeeRate = (
  rate: string,
  cap: string
): string | undefined => {
  const baseError = validateFeeRate(rate);
  if (baseError) {
    return baseError;
  }
  const capMicros = parseFeeRateMicros(cap);
  const rateMicros = parseFeeRateMicros(rate);
  if (capMicros === undefined || rateMicros === undefined) {
    return "Protocol cap is not a valid fee rate";
  }
  if (rateMicros > capMicros) {
    return `Must not exceed the protocol cap ${formatFeeRate(cap)}`;
  }
  return undefined;
};

export const buildApprovalLink = (
  appUrl: string,
  builderId: string,
  maxFeeRate: string
): string => {
  const params = new URLSearchParams({
    builder_id: builderId,
    max_fee_rate: maxFeeRate,
  });
  return `${appUrl.replace(/\/+$/, "")}/builder/approve?${params.toString()}`;
};

export const buildDashboardLink = (appUrl: string): string =>
  `${appUrl.replace(/\/+$/, "")}/builder`;

export interface IPartnerLinks {
  approvalLink?: string;
  // Why the approval link cannot be generated (shown instead of the link).
  approvalDisabledReason?: string;
  dashboardLink?: string;
  dashboardDisabledReason?: string;
}

export const APP_URL_UNKNOWN_REASON =
  "Exchange app URL is unknown for this environment (REACT_APP_ENVIRONMENT)";
export const CAP_NOT_SET_REASON =
  "Set the protocol cap first — builder fees are disabled while it is NOT SET";

/** Derives the partner links for a builder, failing closed on any gap. */
export const partnerLinks = ({
  appUrl,
  builderId,
  status,
  cap,
  maxFeeRate,
}: {
  appUrl: string | undefined;
  builderId: string;
  status: BuilderStatus;
  cap: string | null | undefined;
  maxFeeRate: string;
}): IPartnerLinks => {
  if (!appUrl) {
    return {
      approvalDisabledReason: APP_URL_UNKNOWN_REASON,
      dashboardDisabledReason: APP_URL_UNKNOWN_REASON,
    };
  }
  const links: IPartnerLinks = { dashboardLink: buildDashboardLink(appUrl) };
  if (status !== "ACTIVE") {
    links.approvalDisabledReason = `Builder is ${status}; approvals only work for ACTIVE builders`;
  } else if (cap === null || cap === undefined) {
    links.approvalDisabledReason = CAP_NOT_SET_REASON;
  } else {
    const rateError = validatePartnerFeeRate(maxFeeRate, cap);
    if (rateError) {
      links.approvalDisabledReason = rateError;
    } else {
      links.approvalLink = buildApprovalLink(appUrl, builderId, maxFeeRate);
    }
  }
  return links;
};

export const buildHandoffBlurb = ({
  builderId,
  feeAccount,
  approvalLink,
  dashboardLink,
}: {
  builderId: string;
  feeAccount: string;
  approvalLink?: string;
  dashboardLink?: string;
}): string => [
  `builder_id: ${builderId}`,
  `Fee account: ${feeAccount}`,
  `Approval link (send your users here): ${approvalLink ?? "not available yet"}`,
  `Builder dashboard: ${dashboardLink ?? "not available yet"} (log in with the fee account ${feeAccount})`,
  "Orders must carry builder_id + builder_fee_rate signed with the builder Order EIP-712 type; see API docs.",
].join("\n");

// ---------------------------------------------------------------------------
// V2 oversight: list controls
// ---------------------------------------------------------------------------

export type BuilderStatusFilter = "ALL" | BuilderStatus;
export type BuilderSortKey = "name" | "status";

// ---------------------------------------------------------------------------
// V2 oversight: stats
// ---------------------------------------------------------------------------

export type BuilderStatsPeriod = "24h" | "7d" | "30d" | "90d";
export const BUILDER_STATS_PERIODS: BuilderStatsPeriod[] = ["24h", "7d", "30d", "90d"];

/**
 * "+18.0%" / "-5.0%" change of `current` vs `previous` (decimal strings or
 * counts). "new" when previous is zero and current is not; undefined when
 * either side is missing or unparseable (never render a made-up delta).
 */
export const formatPeriodDelta = (
  current: string | number | undefined,
  previous: string | number | undefined
): string | undefined => {
  if (current === undefined || previous === undefined || current === "" || previous === "") {
    return undefined;
  }
  const cur = Number(current);
  const prev = Number(previous);
  if (!Number.isFinite(cur) || !Number.isFinite(prev)) {
    return undefined;
  }
  if (prev === 0) {
    return cur === 0 ? "0.0%" : "new";
  }
  const change = ((cur - prev) / Math.abs(prev)) * 100;
  const sign = change > 0 ? "+" : "";
  return `${sign}${change.toFixed(1)}%`;
};
